import { useState } from "react";
import { Link } from "wouter";
import { ArrowRight, Building2, Users, Layers, AlertTriangle, LogIn, ChevronRight, Loader2 } from "lucide-react";
import { MarketingLayout } from "../components/MarketingLayout";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../lib/useSEO";
import { useAuth } from "@/hooks/useAuth";
import { getDashboardRouteForRole } from "@/lib/dashboardRoutes";

const DEMO_ORG = {
  name: "Alea Technologies Private Limited",
  industry: "Software & IT Services",
  employees: "250+",
  modules: ["HRMS", "Payroll", "CRM", "Finance", "Procurement", "Inventory", "Analytics"],
  description:
    "A fully pre-configured Indian IT company with three years of realistic enterprise data across every module — payroll runs, leave cycles, procurement workflows, CRM pipelines, financial ledgers and more.",
};

const PERSONAS = [
  {
    name: "Rajesh Sharma",
    role: "CEO / Super Admin",
    email: "rajesh.sharma@aleatech.in",
    password: "Demo@1234",
    access: "Full platform access — all modules, settings, user management",
    color: "from-violet-500 to-purple-700",
    initials: "RS",
  },
  {
    name: "Priya Nair",
    role: "HR Head",
    email: "priya.nair@aleatech.in",
    password: "Demo@1234",
    access: "HRMS, Payroll, Attendance, Leave, Recruitment, Performance",
    color: "from-blue-500 to-blue-700",
    initials: "PN",
  },
  {
    name: "Amit Agarwal",
    role: "Finance Head",
    email: "amit.agarwal@aleatech.in",
    password: "Demo@1234",
    access: "Finance, Accounts, GST/TDS, Banking, Procurement approvals",
    color: "from-emerald-500 to-green-700",
    initials: "AA",
  },
  {
    name: "Vikram Reddy",
    role: "Sales Head",
    email: "vikram.reddy@aleatech.in",
    password: "Demo@1234",
    access: "CRM, Sales pipeline, Leads, Quotes, Customer orders",
    color: "from-orange-500 to-amber-700",
    initials: "VR",
  },
  {
    name: "Neha Gupta",
    role: "Operations Manager",
    email: "neha.gupta@aleatech.in",
    password: "Demo@1234",
    access: "Projects, Work orders, Inventory, Operations tickets",
    color: "from-rose-500 to-red-700",
    initials: "NG",
  },
  {
    name: "Anjali Verma",
    role: "Employee (Self-Service)",
    email: "anjali.verma@aleatech.in",
    password: "Demo@1234",
    access: "My Portal — payslips, leave, attendance, profile, expenses",
    color: "from-cyan-500 to-sky-700",
    initials: "AV",
  },
];

const COMING_SOON_ORGS = [
  {
    name: "Shree Industries Ltd",
    industry: "Manufacturing",
    employees: "500+",
    modules: ["Manufacturing", "Procurement", "Inventory", "Finance"],
  },
  {
    name: "Nexus Retail Pvt Ltd",
    industry: "Retail & Distribution",
    employees: "300+",
    modules: ["Sales", "CRM", "Inventory", "Procurement"],
  },
];

export default function DemoAccess() {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { demoLogin } = useAuth();

  useSEO({
    title: "Demo Access — Explore Alea ERP",
    description:
      "Explore Alea ERP using pre-configured demo organizations. Each contains realistic enterprise data, workflows, employees, payroll, CRM, finance and more.",
    path: "/demo-access",
  });

  async function handleDemoLogin(email: string) {
    setLoading(email);
    setError(null);
    try {
      const loggedInUser = await demoLogin(email);
      const targetRoute = getDashboardRouteForRole(loggedInUser?.roleName || loggedInUser?.role, loggedInUser?.isSuperAdmin);
      window.location.href = targetRoute;
    } catch (e: unknown) {
      console.error("[DemoAccess] Login error:", e);
      setError(e instanceof Error ? e.message : "Demo login failed — please try again");
    } finally {
      setLoading(null);
    }
  }

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0a2d4a] pt-32 pb-20 lg:pt-40">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-96 w-full max-w-[700px] -translate-x-1/2 rounded-full bg-blue-500/20 blur-[120px]" />
        <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-300">
              Demo Environment
            </span>
            <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Explore Alea ERP
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-300">
              Explore Alea ERP using pre-configured demo organizations. Each contains realistic
              enterprise data, workflows, employees, payroll records, CRM pipelines, finance data,
              inventory transactions, reports, dashboards and approvals.
            </p>
            <p className="mt-4 text-base text-blue-200">
              No sign-up required — just pick a persona and log in instantly.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Demo warning banner */}
      <div className="border-b border-amber-200 bg-amber-50">
        <div className="mx-auto flex max-w-7xl items-start gap-3 px-5 py-4 sm:items-center sm:px-8">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 sm:mt-0" />
          <div>
            <span className="text-sm font-semibold text-amber-800">Demo Environment — </span>
            <span className="text-sm text-amber-700">
              This environment contains sample business data for evaluation purposes only. Changes
              may be reset periodically.
            </span>
          </div>
        </div>
      </div>

      {/* Active demo org */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 ring-1 ring-green-200">
              <span className="flex h-2 w-2 rounded-full bg-green-500" />
              Available Now
            </div>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/60 p-6 sm:p-8">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#0a2d4a] to-blue-700 text-white shadow">
                    <Building2 className="h-7 w-7" />
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold text-[#0a2d4a]">{DEMO_ORG.name}</h2>
                    <p className="mt-0.5 text-sm text-slate-500">{DEMO_ORG.industry}</p>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-600">
                      {DEMO_ORG.description}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-4 sm:flex-col sm:items-end">
                  <div className="flex items-center gap-1.5 text-sm text-slate-600">
                    <Users className="h-4 w-4 text-blue-500" />
                    <span className="font-semibold">{DEMO_ORG.employees}</span> employees
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-600">
                    <Layers className="h-4 w-4 text-blue-500" />
                    {DEMO_ORG.modules.length} modules
                  </div>
                </div>
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {DEMO_ORG.modules.map((m) => (
                  <span
                    key={m}
                    className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700"
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Personas */}
      <section className="bg-slate-50 py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-10">
            <h2 className="text-2xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-3xl">
              Choose a demo persona
            </h2>
            <p className="mt-2 text-slate-500">
              Each persona has a different role and access level — explore the platform from every
              perspective.
            </p>
          </Reveal>

          {error && (
            <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PERSONAS.map((p, i) => (
              <Reveal key={p.email} delay={i * 0.06}>
                <div className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                  <div className="flex items-start gap-4">
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${p.color} text-sm font-bold text-white shadow`}
                    >
                      {p.initials}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-[#0a2d4a]">{p.name}</div>
                      <div className="text-xs font-semibold text-blue-600">{p.role}</div>
                    </div>
                  </div>

                  <p className="mt-4 flex-1 text-xs leading-relaxed text-slate-500">{p.access}</p>

                  <button
                    type="button"
                    onClick={() => handleDemoLogin(p.email)}
                    disabled={loading !== null}
                    className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-[#0a2d4a] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#0d3a5f] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading === p.email ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <LogIn className="h-4 w-4" />
                    )}
                    {loading === p.email ? "Signing in…" : `Log in as ${p.name.split(" ")[0]}`}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Coming soon orgs */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-500">
              Coming Soon
            </div>
            <h2 className="mt-3 text-xl font-bold text-[#0a2d4a]">More demo organizations</h2>
            <p className="mt-1 text-sm text-slate-500">
              Additional industry verticals will be available in upcoming releases.
            </p>
          </Reveal>

          <div className="grid gap-4 sm:grid-cols-2">
            {COMING_SOON_ORGS.map((org) => (
              <Reveal key={org.name}>
                <div className="flex items-start gap-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-6 opacity-60">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-600">{org.name}</h3>
                    <p className="text-xs text-slate-400">{org.industry} · {org.employees} employees</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {org.modules.map((m) => (
                        <span key={m} className="rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-slate-50 py-16">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <h2 className="text-2xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-3xl">
              Ready to set up your own organization?
            </h2>
            <p className="mt-3 text-slate-500">
              Create your organization, invite your team and configure Alea ERP for your business.
            </p>
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                href="/sign-up"
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-500"
              >
                Create Your Organization
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-[#0a2d4a] transition-colors hover:bg-slate-50"
              >
                Talk to Sales
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </MarketingLayout>
  );
}
